class RelatedProductsSwiper {
  constructor() {
    this.instances = new Map();
    this.observer = null;
    this.init();
  }

  init() {
    // Initialize any existing instances
    this.initSwiperInstances();
    
    // Set up MutationObserver for dynamic content
    this.setupObserver();
    
    // Listen for section load events from Shopify theme editor
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target;
      const swiperContainer = section.querySelector('[data-related-swiper]');
      if (swiperContainer) {
        this.initSwiperInstances(section);
      }
    });
    
    // Listen for section unload events
    document.addEventListener('shopify:section:unload', (event) => {
      const sectionId = event.detail.sectionId;
      this.destroySwiper(sectionId);
    });
  }

  initSwiperInstances(container = document) {
    const swiperContainers = container.querySelectorAll('[data-related-swiper]');
    
    swiperContainers.forEach(container => {
      const sectionId = container.dataset.relatedSwiper;
      
      // Skip if already initialized
      if (this.instances.has(sectionId)) return;
      
      // Initialize Swiper if there are slides
      const slides = container.querySelectorAll('.swiper-slide');
      if (slides.length > 0) {
        this.initSwiper(container, sectionId);
      }
    });
  }

  initSwiper(container, sectionId) {
    try {
      // Check if Swiper is available
      if (typeof Swiper === 'undefined') {
        console.warn('Swiper is not loaded. Related products carousel will not work.');
        return;
      }

      // Destroy existing instance if it exists
      this.destroySwiper(sectionId);

      const swiperOptions = {
        slidesPerView: 1.2,
        spaceBetween: 10,
        loop: false, // Keep loop disabled to avoid the warning
        watchOverflow: true, // Disable swiper when not enough slides
        navigation: {
          nextEl: `[data-related-swiper-next="${sectionId}"]`,
          prevEl: `[data-related-swiper-prev="${sectionId}"]`,
        },
        // pagination: {
        //   el: `[data-related-swiper-pagination="${sectionId}"]`,
        //   clickable: true,
        // },
        breakpoints: {
          0: {
            slidesPerView: 1.2,
            slidesPerGroup: 1,
          },
          750: {
            slidesPerView: 2.2,
            slidesPerGroup: 2,
          },
          990: {
            slidesPerView: 4,
            slidesPerGroup: 4,
          }
        },
        on: {
          init: () => {
            // Dispatch custom event when swiper is initialized
            container.dispatchEvent(new CustomEvent('swiper:initialized', { detail: { sectionId } }));
            
            // Hide navigation if not enough slides
            const swiper = this.instances.get(sectionId);
            if (swiper) {
              const prevBtn = container.querySelector(`[data-related-swiper-prev="${sectionId}"]`);
              const nextBtn = container.querySelector(`[data-related-swiper-next="${sectionId}"]`);
              
              if (swiper.slides.length <= Math.ceil(swiper.params.slidesPerView)) {
                if (prevBtn) prevBtn.style.display = 'none';
                if (nextBtn) nextBtn.style.display = 'none';
              } else {
                if (prevBtn) prevBtn.style.display = 'flex';
                if (nextBtn) nextBtn.style.display = 'flex';
              }
            }
          }
        }
      };

      const swiper = new Swiper(container, swiperOptions);
      
      // Store reference to this instance
      this.instances.set(sectionId, swiper);
      
      // Add a flag to indicate this container has been initialized
      container.setAttribute('data-swiper-initialized', 'true');
      
    } catch (error) {
      console.error(`Failed to initialize related products swiper (${sectionId}):`, error);
    }
  }

  destroySwiper(sectionId) {
    if (this.instances.has(sectionId)) {
      try {
        const swiper = this.instances.get(sectionId);
        if (swiper && typeof swiper.destroy === 'function') {
          swiper.destroy(true, true);
        }
        this.instances.delete(sectionId);
        
        // Remove initialization flag
        const container = document.querySelector(`[data-related-swiper="${sectionId}"]`);
        if (container) {
          container.removeAttribute('data-swiper-initialized');
        }
      } catch (error) {
        console.error(`Error destroying swiper instance (${sectionId}):`, error);
      }
    }
  }

  setupObserver() {
    // Only set up observer if MutationObserver is supported
    if (typeof MutationObserver === 'undefined') {
      // Fallback: check for elements periodically
      this.setupPolling();
      return;
    }

    // Disconnect any existing observer
    if (this.observer) {
      this.observer.disconnect();
    }

    // Create new observer
    this.observer = new MutationObserver((mutations) => {
      let shouldCheck = false;
      
      mutations.forEach((mutation) => {
        // Check if any nodes were added
        if (mutation.addedNodes.length > 0) {
          shouldCheck = true;
        }
        // Check if any attributes changed on the product-recommendations element
        if (mutation.type === 'attributes' && 
            mutation.target.matches('product-recommendations')) {
          shouldCheck = true;
        }
      });
      
      if (shouldCheck) {
        this.initSwiperInstances();
      }
    });

    // Start observing the document with the configured parameters
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }
  
  setupPolling() {
    // Fallback for browsers without MutationObserver
    const checkInterval = setInterval(() => {
      const uninitialized = document.querySelectorAll(
        '[data-related-swiper]:not([data-swiper-initialized])'
      );
      
      if (uninitialized.length > 0) {
        this.initSwiperInstances();
      }
    }, 1000);
    
    // Store interval ID for cleanup
    this.pollingInterval = checkInterval;
  }

  // Public method to destroy a specific instance
  destroy(sectionId) {
    this.destroySwiper(sectionId);
  }

  // Public method to destroy all instances and clean up
  destroyAll() {
    // Clear any polling interval
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
    
    // Disconnect observer
    if (this.observer) {
      this.observer.disconnect();
    }
    
    // Destroy all swiper instances
    for (const sectionId of this.instances.keys()) {
      this.destroySwiper(sectionId);
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  // Check if Swiper is loaded
  if (typeof Swiper === 'undefined') {
    console.warn('Swiper is not loaded. Related products carousel will not work.');
    return;
  }
  
  // Initialize our swiper manager
  window.relatedProductsSwiper = new RelatedProductsSwiper();
});

// Export for theme editor compatibility
if (typeof window !== 'undefined') {
  window.relatedProductsSwiper = window.relatedProductsSwiper || new RelatedProductsSwiper();
}
